import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import TimeEntryModal from "./TimeEntryModal";
import {
    getEmployeeTimeCardGrouped,
    getEmployeeTimeCardSummary,
    getThisWeekTimeCardSummary,
    getTodayTimeCardSummary,
} from "../../../redux/features/EmployeeTimeCard";
import { getPayperiod } from "../../../redux/features/payperiod";
import {
    convertDecimalHoursToHM,
    formatTimeCardDate,
    formatTimeCardTime,
    getCurrentTime,
} from "../../../utils/employeeTimeCardhelper";
import { ChevronRight, Loader, ArrowLeft, ArrowRight, Users } from "lucide-react";
import Select from "react-select";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { fetchLastPunch } from "../../../redux/features/common";
import { getManagerEmployees } from "../../../redux/features/timesheet/api";
import { ManagerTimesheetEmployee } from "../../../types";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { isPrivilege, UserPrivileges } from "../../../rules";
import { DateTimeUtil } from "../../../utils";

interface TimeEntry {
    date: string;
    day: string;
    duration: string;
    timeRange?: string;
    approved?: boolean;
}

const TimecardMobileView: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { setSourcePath } = useNavigationHistory();
    const user = useSelector((state: RootState) => state?.user?.user);
    const { todaySummary, groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);
    const lastPunchData = useSelector((state: RootState) => state.common.LastPunchState);
    const { employeeRecords } = useSelector((state: RootState) => state.timesheet);

    // State for manager view
    const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(0);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

    const localStoragePayperiod = localStorage.getItem("selectedPayperiod") || "";
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");
    const [selectedPayperiod, setSelectedPayperiod] = useState<string>(localStorage.getItem("selectedPayperiod") || "");
    const [loadMoreCount, setLoadMoreCount] = useState<number>(-5);
    const [payperiodOptions, setPayperiodOptions] = useState<any[]>([]);
    const prevStartDateRef = useRef<string>("");
    const prevEndDateRef = useRef<string>("");
    const [loading, setLoading] = useState<boolean>(true); // Loading for general data
    const [loadingPayperiod, setLoadingPayperiod] = useState<boolean>(false); // Loading for payperiod load more
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

    const isAdmin = isPrivilege(user?.employee_access_role_privileges, UserPrivileges.EMPLOYEE_PRIV_ADMIN);
    const isAllowedToEdit = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET
    );
    const isManagerPrivilege = isPrivilege(
        user?.employee_access_role_privileges,
        UserPrivileges.USER_PRIV_UPDATE_REPORTING_EMP_TIMESHEETS
    );
    const isManager = user?.is_manager;

    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;

    const canEdit = isManager ? managerPrivilege : selfPrivilege;

    // Employee navigation handlers for manager view
    const handlePreviousEmployee = () => {
        if (employeeRecords && employeeRecords.length > 0) {
            setCurrentEmployeeIndex((prev) => (prev > 0 ? prev - 1 : employeeRecords.length - 1));
        }
    };

    const handleNextEmployee = () => {
        if (employeeRecords && employeeRecords.length > 0) {
            setCurrentEmployeeIndex((prev) => (prev < employeeRecords.length - 1 ? prev + 1 : 0));
        }
    };

    const handleLoadMore = () => {
        if (user?.os_emp_account_id) {
            const newCount = loadMoreCount - 5;
            setLoadMoreCount(newCount);
            setLoadingPayperiod(true);
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: newCount })).finally(() => {
                setLoadingPayperiod(false); // Stop payperiod loading once it's done
            });
        }
    };

    useEffect(() => {
        if (user?.os_emp_account_id && !payperiodFilters?.length) {
            // setLoading(true);
            dispatch(getPayperiod({ employee_id: user?.os_emp_account_id, count: loadMoreCount })).finally(() => {
                // setLoading(false); // Stop loading after initial data fetch
            });
        }
    }, [dispatch, user?.os_emp_account_id, loadMoreCount, payperiodFilters?.length]);

    useEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet"));
        const datetime = getCurrentTime(); // example datetime
        dispatch(fetchLastPunch(datetime));

        // Set up proper back navigation - this is the key fix
        // Remember where we came from (likely /mobile-dashboard)
        if (location.state?.from) {
            setSourcePath(location.state.from);
        } else {
            setSourcePath("/mobile-dashboard");
        }

        // Set up global back handler to go back to mobile dashboard
        setGlobalBackHandler(() => {
            navigate("/mobile-dashboard");
            return true; // prevent default back behavior
        });
        
        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [dispatch, navigate, location.state, setSourcePath]);

    //reset local storage if payperiod on date change
    useEffect(() => {
        if (payperiodFilters?.length) {
            const newPayperiodOptions = payperiodFilters.map((payperiod) => ({
                value: `${payperiod.start_datetime} - ${payperiod.end_datetime}`,
                label: `${payperiod.start_date_ui} - ${payperiod.end_date_ui}`,
            }));

            // Get today's date string (e.g., "2025-05-19")
            const today = new Date().toISOString().split("T")[0];
            const storedDate = localStorage.getItem("selectedPayperiodDate");

            let shouldReset = false;

            if (storedDate !== today) {
                // If the date is different or doesn't exist, mark for reset
                shouldReset = true;
                // Update stored date
                localStorage.setItem("selectedPayperiodDate", today);
            }

            const storedPayperiod = localStorage.getItem("selectedPayperiod");

            if (!shouldReset && storedPayperiod) {
                // Use stored value if date is still valid
                const selectedOption = newPayperiodOptions.find((option) => option.value === storedPayperiod);

                if (selectedOption) {
                    setSelectedPayperiod(selectedOption.value);
                    const [startDatetime, endDatetime] = storedPayperiod.split(" - ");
                    handlePayperiodChange(startDatetime, endDatetime);
                }
            } else {
                // If date changed or no stored value, use first option
                const firstOption = newPayperiodOptions[0]?.value;
                if (firstOption) {
                    setSelectedPayperiod(firstOption);
                    localStorage.setItem("selectedPayperiod", firstOption);
                    const [startDatetime, endDatetime] = firstOption.split(" - ");
                    handlePayperiodChange(startDatetime, endDatetime);
                }
            }

            setPayperiodOptions([...newPayperiodOptions, { value: "load-more", label: "Load More" }]);
        }
    }, [payperiodFilters]);

    const handlePayperiodChange = (startDate: string, endDate: string) => {
        // Avoid unnecessary API calls if the start and end date are the same as the previous ones
        if (startDate === prevStartDateRef.current && endDate === prevEndDateRef.current) {
            return;
        }

        // Set loading state to true when starting the API call
        setLoading(true);

        // Update the selected start and end date
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);

        // Update the refs to prevent unnecessary API calls on subsequent renders
        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;

        const id = Number(user?.os_emp_account_id);
        // Create an array of promises for the API calls
        const apiCalls = [
            dispatch(
                getEmployeeTimeCardGrouped({
                    start_date: startDate,
                    end_date: endDate,
                    employee_id: user?.os_emp_account_id,
                    record_type: "A",
                    approval_status: "",
                    sort_by: "os_emp_start_datetime",
                    sort_order: "ASC",
                    group_by: "date",
                    page: "1",
                })
            ),
            dispatch(getTodayTimeCardSummary(id)),
            dispatch(getThisWeekTimeCardSummary(id)),
        ];

        // Wait for all API calls to complete
        Promise.all(apiCalls)
            .then(() => {
                // Once all API calls have completed, set loading to false
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error with API calls:", error);
                // If there is an error, still set loading to false
                setLoading(false);
            });
    };

    useEffect(() => {
        if (user?.os_emp_account_id && payperiodFilters?.length > 0 && !localStoragePayperiod) {
            setLoading(true);
            dispatch(
                getEmployeeTimeCardSummary({
                    employee_id: Number(user?.os_emp_account_id),
                    start_date: payperiodFilters[0]?.start_datetime,
                    end_date: payperiodFilters[0]?.end_datetime,
                })
            ).finally(() => setLoading(false));
        }
    }, [dispatch, user?.os_emp_account_id, payperiodFilters]);

    const handlePayperiodDropdownChange = (selectedOption: any) => {
        if (selectedOption.value === "load-more") {
            handleLoadMore();
        } else {
            setSelectedPayperiod(selectedOption?.value);
            localStorage.setItem("selectedPayperiod", selectedOption?.value);
            const [startDatetime, endDatetime] = selectedOption.value.split(" - ");
            handlePayperiodChange(startDatetime, endDatetime);
        }
    };

    // When employee records change or current index changes, update the selected employee
    useEffect(() => {
        if (employeeRecords && employeeRecords.length > 0) {
            const employee = employeeRecords[currentEmployeeIndex];
            if (employee && employee.emp_detail?.employee_id) {
                setSelectedEmployeeId(employee.emp_detail.employee_id);
            }
        }
    }, [employeeRecords, currentEmployeeIndex]);

    // When selected employee changes, fetch their timecard data
    useEffect(() => {
        if (selectedEmployeeId && selectedStartDate && selectedEndDate) {
            dispatch(
                getEmployeeTimeCardGrouped({
                    start_date: selectedStartDate,
                    end_date: selectedEndDate,
                    employee_id: selectedEmployeeId,
                    record_type: "A",
                    approval_status: "",
                    sort_by: "os_emp_start_datetime",
                    sort_order: "ASC",
                    group_by: "date",
                    page: "1",
                })
            );
        }
    }, [selectedEmployeeId, selectedStartDate, selectedEndDate, dispatch]);

    return (
        // <div id="refresh-container" className="h-full overflow-y-auto bg-white">
        <div className="container mx-auto bg-white h-full flex flex-col">
            <div className="flex flex-col text-gray-800 text-base font-sans h-full">
                <div className="pb-0">
                    {/* <div className="text-center font-medium text-lg text-blue-600 mb-2">
          My Info
        </div>
        <div className="flex justify-around border-b mb-2 pb-1 text-gray-600 text-sm">
          <div>Job</div>
          <div>Time Off</div>
          <div className="text-blue-600 border-b-2 border-blue-600 pb-1">
            Timesheet
          </div>
          <div>Benefits</div>
          <div>Emergency</div>
        </div> */}
                    <div className="text-center text-sm mb-2">
                        {/* Show:{" "} */}
                        <span className="text-blue-500">
                            <div className="relative w-full px-4 mx-auto pt-4">
                                {/* <label
                htmlFor="payperiodDropdown"
                className="block text-sm font-medium text-gray-700 mb-2 text-left"
              >
                Select Pay Period
              </label> */}

                                <Select
                                    inputId="payperiodDropdown"
                                    value={payperiodOptions?.find((option) => option?.value === selectedPayperiod)}
                                    onChange={handlePayperiodDropdownChange}
                                    options={payperiodOptions} // Dynamically updated options
                                    className="block w-full"
                                    classNamePrefix="react-select"
                                    isSearchable={false}
                                    placeholder="Select a pay period"
                                />
                            </div>
                        </span>
                    </div>
                </div>
                {/* Loader for payperiod load more */}
                {loading && (
                    <div className="flex justify-center items-center py-4">
                        <Loader
                            size={32}
                            color="#2664eb"
                            strokeWidth={2.5}
                            absoluteStrokeWidth
                            className="animate-spin"
                        />
                    </div>
                )}

                {!loading && (
                    <div className="flex flex-col flex-1 h-full overflow-hidden">
                        {" "}
                        {/* Today's summary */}
                        <div className="bg-gray-100 p-4 rounded-lg mb-4 flex-shrink-0">
                            <div className="text-2xl font-medium text-center">
                                {convertDecimalHoursToHM(todaySummary?.total_hours ?? 0)} Today
                            </div>
                            <div className="text-center text-sm text-gray-600 mt-1">
                                Clocked {lastPunchData?.punch_status === "0" ? "In" : "Out"}:{" "}
                                {formatTimeCardTime(lastPunchData?.datetime || "")},{" "}
                                {formatTimeCardDate(lastPunchData?.datetime || "")}
                            </div>
                            {/* <div className="mt-4 flex justify-around items-center">
            <button
              className="bg-blue-500 text-white py-1 px-6 rounded-full text-base font-medium"
              onClick={() => alert("Clock In clicked")}
            >
              🕒 Clock In
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="border border-blue-600 text-blue-500 py-1 px-4 rounded-full text-base font-medium"
            >
              Add Entry
            </button>
          </div> */}
                        </div>
                        <div className="grid grid-cols-1 text-center text-base font-semibold mb-2 flex-shrink-0">
                            {/* <div>
              <div className="text-gray-600 text-sm">This Week</div>
              <div className="text-lg">
                {convertDecimalHoursToHM(thisWeekSummary?.total_hours ?? 0)}
              </div>
            </div> */}
                            <div>
                                <div className="text-gray-600 text-sm">Pay Period Time</div>
                                <div className="text-lg">
                                    {convertDecimalHoursToHM(
                                        groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0
                                    )}
                                </div>
                            </div>
                            {canEdit && (
                                <div>
                                    <button
                                        className=" bg-blue-600 text-white px-4 py-2 rounded-lg font-medium text-sm mt-1"
                                        onClick={() => navigate("/m-add-timesheet-entry")}
                                    >
                                        Add entry
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Render grouped timesheet entries */}
                        <div className="overflow-y-auto flex-1 px-4 pb-16 scrollbar-none">
                            {groupedTimesheets?.data?.timesheet_entries_by_date &&
                            Object.keys(groupedTimesheets.data.timesheet_entries_by_date).length === 0 ? (
                                <div>No timecard data available.</div>
                            ) : (
                                Object.entries(groupedTimesheets?.data?.timesheet_entries_by_date || {}).map(
                                    ([dateKey, entries]) => {
                                        const totalHours = entries.reduce(
                                            (acc, entry) => acc + (Number(entry?.hours) || 0),
                                            0
                                        );
                                        return (
                                            <div key={dateKey} className="border-t flex items-start mb-4 py-1">
                                                <div className="w-2/6 font-medium text-lg text-blue-500">
                                                    {DateTimeUtil.formatShortDate(dateKey)}
                                                </div>
                                                <div className="w-4/6">
                                                    <div className="text-base">
                                                        <div className="text-lg text-gray-500 font-medium">
                                                            {convertDecimalHoursToHM(totalHours)}
                                                        </div>
                                                        {entries.map((entry: any, index: number) => (
                                                            <div
                                                                key={index}
                                                                className="text-base cursor-pointer pt-1"
                                                                // onClick={() => handleEntryClick(entry)}
                                                            >
                                                                <div
                                                                    className="items-center"
                                                                    onClick={() => {
                                                                        navigate(
                                                                            `/m-timecard-record-detail/${entry?.id}`
                                                                        );
                                                                    }}
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <div className="text-sm text-gray-500 flex items-center">
                                                                            {formatTimeCardTime(
                                                                                entry?.start_datetime || ""
                                                                            )}{" "}
                                                                            –{" "}
                                                                            {formatTimeCardTime(
                                                                                entry?.end_datetime || ""
                                                                            )}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500 flex items-center">
                                                                            ({convertDecimalHoursToHM(entry?.hours)}){" "}
                                                                            <ChevronRight
                                                                                className="cursor-pointer"
                                                                                size={16}
                                                                                color="#000032"
                                                                                strokeWidth={1}
                                                                                absoluteStrokeWidth
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {/* <div
                    className="cursor-pointer flex items-center justify-center text-gray-500"
                    onClick={() => navigate(`/test-daydetail/${dateKey}`)}
                  >
                    <ChevronRight />
                  </div> */}
                                                </div>
                                            </div>
                                        );
                                    }
                                )
                            )}
                        </div>
                    </div>
                )}

                {isModalOpen && <TimeEntryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
            </div>
        </div>
        // </div>
    );
};

export default TimecardMobileView;
